import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight'
import { faGlobe } from '@fortawesome/pro-solid-svg-icons/faGlobe'
import { faQuestionSquare } from '@fortawesome/pro-solid-svg-icons/faQuestionSquare'
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { Box, Flex, Menu, Text } from '@mantine/core'
import Link from 'next/link'
import { useEffect } from 'react'

import { useUserLogout } from '@flecto/auth'
import { useQueryGetUserLogged } from '@flecto/core/shared'
import { useTranslation, i18n, useLanguageState } from '@flecto/translations'
import { type CoreLocale } from '@flecto/types'

import { useBookingStore } from '../../../../flecto-modules-saas/src/booking-details/hooks/useBookingStore'
import { AvatarUserLogged, Icon, useNotifications } from '../../../@shared'

export function UserMenu() {
  const { showNotification } = useNotifications()

  const { setUser } = useBookingStore()
  const { t } = useTranslation('saas', { keyPrefix: 'general' })

  const { data, isLoading: userLoading } = useQueryGetUserLogged()

  const logout = useUserLogout()

  const { changeLanguage, supportedLanguages, currentLanguage } =
    useLanguageState()

  const handleChangeLanguage = async (nextLocale: CoreLocale) => {
    await changeLanguage(nextLocale)

    showNotification({
      type: 'success',
      title: t('user.change_language_title'),
      message: t('user.change_language_description'),
    })
  }

  useEffect(() => {
    if (!userLoading && data) {
      setUser(data)
    }
  },[data, userLoading, setUser])

  if (userLoading || !data) {
    return <></>
  }

  return (
    <Menu
      shadow='md'
      width={220}
      trigger='hover'
      position='right-end'
      withinPortal
    >
      <Menu.Target>
        <Box>
          <AvatarUserLogged size='md' radius='100%' />
        </Box>
      </Menu.Target>
      <Menu.Dropdown style={{ zIndex: 1010 }}>
        <Menu.Item
          href='/profile/user'
          passHref
          leftSection={<Icon icon={faUser} />}
          component={Link}
        >
          <Text pl='sm'>{t('user.profile')}</Text>
        </Menu.Item>
        <Menu.Item
          leftSection={<Icon icon={faQuestionSquare} />}
          component={Link}
          href={`https://intercom.help/flecto/${i18n.language}`}
          passHref
        >
          <Text pl='sm'>{t('user.help_center')}</Text>
        </Menu.Item>
        <Menu.Item
          rightSection={<Icon icon={faChevronRight} />}
          leftSection={<Icon icon={faGlobe} />}
        >
          <Menu position='right-start' offset={30} trigger='hover'>
            <Menu.Target>
              <Text pl='sm'>{t('user.languages')}</Text>
            </Menu.Target>
            <Menu.Dropdown mt={-18} style={{ zIndex: 1030 }}>
              {supportedLanguages.map((language) => (
                <Menu.Item
                  key={language.locale}
                  onClick={() => handleChangeLanguage(language.locale)}
                  w='200px'
                >
                  <Flex justify='space-between' align='center'>
                    <Flex align='center' gap='md'>
                      <Box>{language.flag}</Box>
                      <Box>{language.name}</Box>
                    </Flex>
                    {language.locale === currentLanguage.locale && (
                      <Icon icon={faCheck} />
                    )}
                  </Flex>
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item
          leftSection={<Icon icon={faSignOut} />}
          onClick={() =>
            logout({ logoutParams: { returnTo: 'https://flecto.io' } })
          }
        >
          <Text pl='sm'>{t('user.logout')}</Text>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}
